import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CustomerService } from '../services/customer.service';
import { CustomerFavoriteItemDTO } from '../models/customer.model';

const LOCAL_FAVORITES_KEY = 'guest_favorite_items';

@Injectable()
export class CustomerStore {
  private favoriteItemsSubject = new BehaviorSubject<string[]>([]);
  public readonly favoriteItems$ = this.favoriteItemsSubject.asObservable();

  private isPrivateCustomerSubject = new BehaviorSubject<boolean>(false);
  public readonly isPrivateCustomer$ = this.isPrivateCustomerSubject.asObservable();

  private customerLoadingSubject = new BehaviorSubject<boolean>(false);
  public customerLoading$ = this.customerLoadingSubject.asObservable();

  private giftCardOrders = new BehaviorSubject<any[]>([]);
  public readonly giftCardOrders$ = this.giftCardOrders.asObservable();

  constructor(private customerService: CustomerService) {}

  public setCustomerLoading(value: boolean): void {
    this.customerLoadingSubject.next(value);
  }

  public getCustomerLoadingValue(): boolean {
    return this.customerLoadingSubject.getValue();
  }

  get favoriteItems(): string[] {
    return this.favoriteItemsSubject.value;
  }

  public loadLocalFavoriteItems(): void {
    try {
      const raw = localStorage.getItem(LOCAL_FAVORITES_KEY);
      const items = raw ? JSON.parse(raw) : [];
      const normalizedItems = Array.isArray(items)
        ? this.normalizeFavoriteItems(items)
        : [];

      this.favoriteItemsSubject.next(normalizedItems);
      this.saveLocalFavoriteItems(normalizedItems);
    } catch (error) {
      console.error('Error loading local favorite items', error);
      this.favoriteItemsSubject.next([]);
    }
  }

  private saveLocalFavoriteItems(items: string[]): void {
    try {
      localStorage.setItem(LOCAL_FAVORITES_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('Error saving local favorite items', error);
    }
  }

  public clearLocalFavoriteItems(): void {
    try {
      localStorage.removeItem(LOCAL_FAVORITES_KEY);
    } catch (error) {
      console.error('Error clearing local favorite items', error);
    }
  }

  public toggleLocalFavoriteItem(itemId: string): void {
    const currentFavorites = this.normalizeFavoriteItems(
      this.favoriteItemsSubject.getValue()
    );

    let updatedFavorites: string[];

    if (currentFavorites.includes(itemId)) {
      updatedFavorites = currentFavorites.filter(item => item !== itemId);
    } else {
      updatedFavorites = [...currentFavorites, itemId];
    }

    updatedFavorites = this.normalizeFavoriteItems(updatedFavorites);

    this.favoriteItemsSubject.next(updatedFavorites);
    this.saveLocalFavoriteItems(updatedFavorites);
  }

  // --------------------------
  // Server favorites
  // --------------------------
  public loadFavoriteItems(customerId: string): void {
    this.customerService.getCustomerFavoriteItems(customerId).subscribe(
      (items) => {
        this.favoriteItemsSubject.next(this.normalizeFavoriteItems(items || []));
      },
      (error) => console.error('Error loading favorite items', error)
    );
  }

  public addFavoriteItem(
    customerId: string,
    favoriteItem: CustomerFavoriteItemDTO,
    finalize?: () => void
  ): void {
    const itemId = favoriteItem.itemId;

    const currentFavorites = this.normalizeFavoriteItems(
      this.favoriteItemsSubject.getValue()
    );

    if (!currentFavorites.includes(itemId)) {
      this.favoriteItemsSubject.next([...currentFavorites, itemId]);
    }

    this.customerService.saveCustomerFavoriteItem(customerId, favoriteItem).subscribe(
      () => {
        this.loadFavoriteItems(customerId);
        finalize?.();
      },
      (error) => {
        console.error('Error saving favorite item', error);
        this.loadFavoriteItems(customerId);
        finalize?.();
      }
    );
  }

  public removeFavoriteItem(
    customerId: string,
    itemId: string,
    finalize?: () => void
  ): void {
    const currentFavorites = this.normalizeFavoriteItems(
      this.favoriteItemsSubject.getValue()
    );

    this.favoriteItemsSubject.next(
      currentFavorites.filter(favoriteItemId => favoriteItemId !== itemId)
    );

    this.customerService.deleteCustomerFavoriteItem(customerId, itemId).subscribe(
      () => {
        this.loadFavoriteItems(customerId);
        finalize?.();
      },
      (error) => {
        console.error('Error deleting favorite item', error);
        this.loadFavoriteItems(customerId);
        finalize?.();
      }
    );
  }

  public toggleFavoriteItem(itemId: string): void {
    this.toggleLocalFavoriteItem(itemId);
  }

  public isPrivateCustomer(merchantId: string, customerId: string): void {
    this.customerService.isPrivateGroup(merchantId, customerId).subscribe(
      (isPrivate) => this.isPrivateCustomerSubject.next(isPrivate),
      () => {
        this.isPrivateCustomerSubject.next(false);
      }
    );
  }

  public loadGiftCardOrders(ganList: string[]): void {
    this.customerService.getCustomerGiftCardOrders(ganList).subscribe(
      (items) => this.giftCardOrders.next(items),
      (error) => console.error('Error loading gift card orders', error)
    );
  }

  public syncLocalFavoritesToCustomer(customerId: string): void {
    try {
      const raw = localStorage.getItem(LOCAL_FAVORITES_KEY);
      const localItems: string[] = this.normalizeFavoriteItems(raw ? JSON.parse(raw) : []);

      if (!localItems.length) {
        this.loadFavoriteItems(customerId);
        return;
      }

      let completed = 0;
      const total = localItems.length;

      localItems.forEach(itemId => {
        this.customerService
          .saveCustomerFavoriteItem(customerId, new CustomerFavoriteItemDTO(itemId))
          .subscribe({
            next: () => {
              completed++;
              if (completed === total) {
                this.clearLocalFavoriteItems();
                this.loadFavoriteItems(customerId);
              }
            },
            error: () => {
              completed++;
              if (completed === total) {
                this.clearLocalFavoriteItems();
                this.loadFavoriteItems(customerId);
              }
            }
          });
      });
    } catch (error) {
      console.error('Error syncing local favorites', error);
      this.loadFavoriteItems(customerId);
    }
  }

  private normalizeFavoriteItems(items: string[]): string[] {
    return Array.from(new Set((items || []).filter(Boolean)));
  }
}
